import { ComponentProps, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Icon, color } from '@uniquegood/realworld-studio-design';
import { createPortal } from 'react-dom';

import {
  ContextMenuItemDestructive,
  ContextMenuItemStyle,
  ContextMenuStyle
} from '../flow/styles';

type ContextMenuIcon = ComponentProps<typeof Icon>['icon'];

interface ContextMenuItem {
  label: string;
  icon: ContextMenuIcon;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

interface MissionListContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
  boundaryElement?: HTMLElement | null;
}

const VIEWPORT_MARGIN = 8;

function MissionListContextMenu({
  x,
  y,
  items,
  onClose,
  boundaryElement
}: MissionListContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ x, y });

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const boundaryRect = boundaryElement?.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;
    const rightBoundary = boundaryRect?.right ?? window.innerWidth;
    const overflowsRight = x + menuWidth + VIEWPORT_MARGIN > rightBoundary;
    const overflowsBottom = y + menuHeight + VIEWPORT_MARGIN > window.innerHeight;
    const nextX = overflowsRight ? x - menuWidth : x;
    const nextY = overflowsBottom ? y - menuHeight : y;

    setPosition({
      x: Math.max(VIEWPORT_MARGIN, nextX),
      y: Math.max(VIEWPORT_MARGIN, nextY)
    });
  }, [boundaryElement, x, y, items.length]);

  useEffect(() => {
    const handleClose = () => onClose();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('click', handleClose);
    document.addEventListener('contextmenu', handleClose, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClose);
      document.removeEventListener('contextmenu', handleClose, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      css={[ContextMenuStyle, ContextMenuSurfaceStyle]}
      style={{ top: position.y, left: position.x }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map(({ label, icon, onClick, destructive, disabled }) => (
        <button
          key={label}
          type="button"
          css={[
            ContextMenuItemStyle,
            ContextMenuItemCompactStyle,
            destructive && ContextMenuItemDestructive,
            disabled && ContextMenuItemDisabledStyle
          ]}
          disabled={disabled}
          role="menuitem"
          onClick={() => {
            if (disabled) return;
            onClose();
            onClick();
          }}
        >
          <Icon icon={icon} size="14px" />
          {label}
        </button>
      ))}
    </div>
  );

  return createPortal(menu, document.body);
}

export default memo(MissionListContextMenu);

const ContextMenuSurfaceStyle = css`
  z-index: 9999;
  min-width: 128px;
`;

const ContextMenuItemCompactStyle = css`
  gap: 6px;
  min-height: 32px;
  padding: 7px 12px;
  white-space: nowrap;

  & > .IconWrap {
    flex: 0 0 16px;
    width: 16px;
    margin: 0;
    justify-content: center;
  }
`;

const ContextMenuItemDisabledStyle = css`
  color: ${color.text_disabled};
  cursor: not-allowed;
`;
